/**
 * SocialPulseProfileOnboard — /onboarding/profile
 *
 * Post-signup full-page prompt offering the user their SocialPulse
 * profile page. If they accept, they're claim their @handle and land on
 * their profile in edit mode. If they skip, we remember it so they only
 * see the dismissible banner in future visits.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, Sparkles, Users, Award, Link2, Loader2, ArrowRight,
  ShoppingBag, Calendar, X,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function SocialPulseProfileOnboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Claim your SocialPulse profile";
  }, []);

  async function choose(choice) {
    if (busy) return;
    setBusy(true);
    try {
      await http.post("/me/profile-onboarded", { choice });
      if (refresh) await refresh();
    } catch { /* non-fatal */ }
    if (choice === "claimed" && user?.handle) {
      nav(`/pulse/${user.handle}?welcome=1`);
    } else {
      nav("/");
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden grid place-items-center px-5 py-10"
      style={{ background: "linear-gradient(140deg, #F0F6FF 0%, #F5F1FA 40%, #ECFDF5 100%)" }}
      data-testid="sp-profile-onboard"
    >
      {/* orbs */}
      <div aria-hidden className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-50 pointer-events-none" style={{ background: "radial-gradient(circle,#A7F3D0 0%,transparent 70%)" }} />
      <div aria-hidden className="absolute top-1/3 right-0 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-45 pointer-events-none" style={{ background: "radial-gradient(circle,#DDD6FE 0%,transparent 75%)" }} />

      <button
        onClick={() => choose("skipped")}
        data-testid="sp-onboard-close"
        aria-label="Close"
        className="absolute top-5 right-5 w-9 h-9 grid place-items-center rounded-full bg-white/70 ring-1 ring-slate-200 hover:ring-violet-400 text-slate-600 hover:text-violet-700"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative max-w-2xl w-full">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur ring-1 ring-emerald-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
            <Sparkles className="w-3 h-3" /> Welcome to SocialPulse
          </span>
          <h1 className="mt-4 font-serif font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl leading-[1.05]" data-testid="sp-onboard-h1">
            Hi <span className="text-violet-700">{user.first_name || user.name?.split(" ")[0] || user.handle}</span>,<br/>
            claim your <span className="text-emerald-700">SocialPulse profile</span>?
          </h1>
          <p className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-lg mx-auto">
            One profile, one identity — visible across every SocialPulse
            Network site. It's optional, and you can start with just the
            basics.
          </p>
          <p className="mt-2 text-sm text-violet-700 font-semibold">
            socialpulse.com/@{user.handle}
          </p>
        </div>

        <ul className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Perk icon={Users}       label="Connect"       tint="violet" />
          <Perk icon={Award}       label="Earn badges"   tint="amber" />
          <Perk icon={ShoppingBag} label="Sell & share"  tint="emerald" />
          <Perk icon={Calendar}    label="Host events"   tint="orange" />
        </ul>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => choose("claimed")}
            disabled={busy}
            data-testid="sp-onboard-claim"
            className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white font-semibold px-6 py-3 text-sm shadow-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            Create my profile <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => choose("skipped")}
            disabled={busy}
            data-testid="sp-onboard-skip"
            className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-6 py-3 text-sm"
          >
            Maybe later
          </button>
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">
          Only your name and @handle are public by default. Everything else stays hidden until you turn it on.
        </p>
      </div>
    </div>
  );
}

const TINTS = {
  violet:  "bg-violet-50 text-violet-700 ring-violet-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  orange:  "bg-orange-50 text-orange-700 ring-orange-200",
  amber:   "bg-amber-50 text-amber-700 ring-amber-200",
};

function Perk({ icon: Icon, label, tint }) {
  return (
    <li className={`flex flex-col items-center gap-2 rounded-2xl ring-1 ${TINTS[tint]} p-3 text-center bg-white/60 backdrop-blur`}>
      <Icon className="w-5 h-5" strokeWidth={2.2} />
      <span className="text-xs font-semibold">{label}</span>
    </li>
  );
}

/* ─────────────────── Banner ─────────────────── */

export function SocialPulseProfileBanner() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  if (!user || dismissed) return null;
  if (user.profile_onboard_choice) return null;

  async function skip() {
    setDismissed(true);
    try {
      await http.post("/me/profile-onboarded", { choice: "skipped" });
      if (refresh) await refresh();
    } catch { /* non-fatal */ }
  }

  return (
    <div
      className="relative rounded-2xl ring-1 ring-violet-200 p-4 sm:p-5 flex items-start gap-4"
      style={{ background: "linear-gradient(135deg,#F5F1FA 0%,#EEF2FF 55%,#ECFDF5 100%)" }}
      data-testid="sp-profile-banner"
    >
      <span className="w-11 h-11 grid place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-violet-600 text-white shrink-0 shadow-sm">
        <Activity className="w-5 h-5" strokeWidth={2.5} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-serif font-bold text-slate-900 text-base sm:text-lg leading-tight">
          Claim your @{user.handle} profile
        </p>
        <p className="mt-0.5 text-sm text-slate-600">
          Your SocialPulse profile lives at{" "}
          <span className="font-semibold text-violet-700">socialpulse.com/@{user.handle}</span> — set it up in under a minute.
        </p>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <a
          href={`/pulse/${user.handle}`}
          data-testid="sp-banner-claim"
          className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 text-xs shadow-sm"
        >
          Set up <Link2 className="w-3 h-3" />
        </a>
        <button
          onClick={skip}
          data-testid="sp-banner-dismiss"
          aria-label="Dismiss"
          className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/60 text-slate-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
